import { Suspense, lazy, useEffect, useState } from "react";
import {
  HashRouter,
  Navigate,
  Outlet,
  Route,
  Routes,
  useNavigate,
} from "react-router-dom";
import Loading from "./components/Loading";
import { updateACL } from "./config/redux/acl/aclSlice";
import store from "./config/redux/store";
import AddInbound from "./pages/auth/Inbound/add";
import DetailInbound from "./pages/auth/Inbound/detail";
import StartInbound from "./pages/auth/Inbound/start";
import DetailInvoice from "./pages/auth/Invoice/detail";
import DetailInvoiceClient from "./pages/auth/InvoiceClient/detail";
import Notifications from "./pages/auth/Notifications";
import AddOutbound from "./pages/auth/Outbound/add";
import DetailOutbound from "./pages/auth/Outbound/detail";
import StartOutbound from "./pages/auth/Outbound/start";
import Profile from "./pages/auth/Profile";
import AddRequestInbound from "./pages/auth/RequestInbound/add";
import DetailRequestInbound from "./pages/auth/RequestInbound/detail";
import AddRequestOutbound from "./pages/auth/RequestOutbound/add";
import DetailRequestOutbound from "./pages/auth/RequestOutbound/detail";
import DetailStock from "./pages/auth/StockOpname/detail-stock";
import Calculator from "./pages/noauth/Calculator";
import Policy from "./pages/noauth/Policy";
import { MENU_LIST_CLIENTS } from "./routes/clients";
import { MENU_LIST } from "./routes/constant";
import { MENU_LIST_INTERNAL } from "./routes/internal";
import API from "./services/api-request";
import { filterMenuList } from "./utils/filterRoute";
import {
  getDataLogin as fetchLoginData,
  getDataLogin,
} from "./utils/getDataLogin";
import { Button, Card } from "antd";
import { LockOutlined } from "@ant-design/icons";
import { CardBody, CardTitle } from "react-bootstrap";

// Pages
const Layout = lazy(() => import("./layouts"));
const Login = lazy(() => import("./pages/noauth/Login"));
const Adon = lazy(() => import("./pages/noauth/Adon"));
const Log = lazy(() => import("./pages/noauth/Log"));
const Utils = lazy(() => import("./pages/noauth/Utils"));

const AfterLogin = () => {
  const navigate = useNavigate();
  useEffect(() => {
    if (!fetchLoginData()) {
      navigate("login");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  return <>{fetchLoginData() ? <Outlet /> : <Navigate to="/login" />}</>;
};

const NoAuth = () => {
  return <>{fetchLoginData() ? <Navigate to="/dashboard" /> : <Outlet />}</>;
};

function App() {
  const handleLogout = () => {
    localStorage.removeItem("data-login");
    window.location.reload();
  };

  // Render Selected Menu
  const renderRoutes = (menuList) => {
    return menuList
      .filter((e) => e.path !== "/calculator") // Exclude Calculator dari layout
      .map((e) => {
        if (e.children && e.children.length > 0) {
          const childRoutes = renderRoutes(e.children);
          return (
            <Route key={e.name} element={e.element}>
              {childRoutes}
            </Route>
          );
        }
        return <Route key={e.name} path={e.path} element={e.element} />;
      });
  };
  const [dataLogin, setDataLogin] = useState(fetchLoginData()); // Ambil data login awal
  const [routes, setRoutes] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const routeAPI = async (MENU_LIST) => {
    setIsLoading(true);
    await API.Get(`control-list/id_privilege/${getDataLogin()?.id_privilege}`)
      .then((result) => {
        const menuFromAPI = result.data.menu;

        setRoutes(filterMenuList(MENU_LIST, menuFromAPI));
        store.dispatch(updateACL(result.data));
      })
      .catch((err) => {
        console.error(err);
        return [];
      })
      .finally(() => setIsLoading(false));
  };

  // Pantau perubahan data login untuk menentukan rute
  useEffect(() => {
    if (dataLogin) {
      if (dataLogin?.company_id === null) {
        setRoutes(MENU_LIST_INTERNAL);
      } else if (dataLogin?.id_privilege === 3) {
        routeAPI(MENU_LIST_CLIENTS);
      } else {
        routeAPI(MENU_LIST);
      }
    } else {
      setRoutes(MENU_LIST);
    }
  }, [dataLogin]);

  // Listener untuk perubahan `localStorage`
  useEffect(() => {
    const handleStorageChange = () => {
      setDataLogin(fetchLoginData()); // Perbarui state data login
    };

    window.addEventListener("storage", handleStorageChange);
    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
  }, []);

  if (isLoading) {
    return <Loading />;
  }

  return (
    <HashRouter>
      <Suspense fallback={<Loading />}>
        {routes?.length > 0 ? (
          <Routes>
            {/* Main Layout with Menu */}
            <Route element={<AfterLogin />}>
              {routes?.length > 0 && (
                <Route element={<Layout />}>{renderRoutes(routes)}</Route>
              )}

              <Route element={<Layout />}>
                {/* Profile */}
                <Route exact path="/my-profile" element={<Profile />} />
                <Route
                  exact
                  path="/inbound/detail/:id"
                  element={<DetailInbound />}
                />
                <Route exact path="/inbound/add" element={<AddInbound />} />
                <Route
                  exact
                  path="/inbound/start/:id"
                  element={<StartInbound />}
                />
                <Route
                  exact
                  path="/request-inbound/detail/:id"
                  element={<DetailRequestInbound />}
                />
                <Route
                  exact
                  path="/request-inbound/add"
                  element={<AddRequestInbound />}
                />
                <Route
                  exact
                  path="/outbound/detail/:id"
                  element={<DetailOutbound />}
                />
                <Route exact path="/outbound/add" element={<AddOutbound />} />
                <Route
                  exact
                  path="/outbound/start/:id"
                  element={<StartOutbound />}
                />
                <Route
                  exact
                  path="/request-outbound/detail/:id"
                  element={<DetailRequestOutbound />}
                />
                <Route
                  exact
                  path="/request-outbound/add"
                  element={<AddRequestOutbound />}
                />
                <Route
                  exact
                  path="/transaction/detail/:id"
                  element={<DetailInvoice />}
                />
                <Route
                  exact
                  path="/transaction-client/detail/:id"
                  element={<DetailInvoiceClient />}
                />
                <Route
                  exact
                  path="/notifications"
                  element={<Notifications />}
                />
                <Route
                  exact
                  path="/detail-stock/:location"
                  element={<DetailStock />}
                />
              </Route>
            </Route>

            <Route element={<NoAuth />}>
              <Route exact path="/login" element={<Login />} />
              {/* end master data  */}
            </Route>

            {/* Catch-All Route */}
            <Route path="/adon" element={<Adon />} />
            <Route path="/log" element={<Log />} />
            <Route path="/utils" element={<Utils />} />
            <Route path="/calculator" element={<Calculator />} />
            <Route path="/policy-public" element={<Policy />} />

            <Route path="*" element={<Navigate to="/login" />} />
          </Routes>
        ) : (
          <div
            style={{
              height: "100vh",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "#f5f5f5",
              padding: 16,
            }}
          >
            <Card
              style={{
                maxWidth: 400,
                width: "100%",
                textAlign: "center",
                borderRadius: 12,
                boxShadow: "0 4px 10px rgba(0,0,0,0.1)",
              }}
            >
              <LockOutlined style={{ fontSize: 40, color: "#faad14" }} />
              <CardTitle level={4} style={{ marginBottom: 8 }}>
                Akses Ditolak
              </CardTitle>
              <CardBody>
                Role ini belum memiliki akses menu. Harap hubungi admin untuk
                mendapatkan izin.
              </CardBody>
              <div style={{ marginTop: 24 }}>
                <Button type="primary" onClick={handleLogout}>
                  Keluar
                </Button>
              </div>
            </Card>
          </div>
        )}
      </Suspense>
    </HashRouter>
  );
}

export default App;
